import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import ListHeader from "@/common/components/ListHeader";
import ListFilters from "@/common/components/ListFilters";
import TextField from "@/common/components/TextField";
import { DatePickerWithRange } from "@/common/components/ui/dateRangePicker";
import useDebounce from "@/common/hooks/useDebounce";
import { useFilterDate } from "@/common/hooks/useFilterDate";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { Mapper } from "@/common/services/mapper";
import { SearchIcon, ShoppingBasket } from "lucide-react";
import { RewardItemData, RewardItemTableItem } from "@/common/types/rewardItems";
import { useRewardItems } from "@/common/hooks/queries/useRewardItems";
import { RewardItemsTransformer } from "../../services/rewardItemsTransformer";
import { RewardItemsGridTable } from "../../components/RewardItemsGridTable";
import Button from "@/common/components/Button";
import { useNavigate } from "react-router-dom";
import { RedeemRewardItemModal } from "../../components/RedeemRewardItemModal";
import { rewardItemsStatusOptions } from "../../constants/rewardItemsStatusOptions.constant";
import { useEstablishmentCompanies } from "@/features/establishments/hooks/useEstablishmentCompanies";
import { EstablishmentCompanyTransformer } from "@/features/establishments/services/establishmentCompanyTransformer";
import { Option } from "@/common/types/option";

const RewardItems = () => {
  const { filterParams, setFilterParams } = useFilterParams();
  const navigate = useNavigate()

  const [search, setSearch] = useDebounce(filterParams.search || '');
  const [searchEstablishment, setSearchEstablishment] = useDebounce('');

  const { date, formattedDate } = useFilterDate(filterParams);

  const { data: establishmentOptions } = useEstablishmentCompanies({
    filters: {
      search: searchEstablishment,
      tmaisModuleEnabled: true,
    },
    mapper: EstablishmentCompanyTransformer.optionTransformer({ valueFieldName: 'matrix_branch_id' }),
  });

  const {
    data: rewardItems,
    meta,
    isPending: rewardItemsIsPending,
    isLoading: rewardItemsIsLoading,
    error: rewardItemsError = {} as any,
    refetch: refetchRewardItems,
  } = useRewardItems({
    filters: {
      search,
      page: filterParams.page || 1,
      status: filterParams.status?.value,
      establishmentCompanyBranchId: filterParams.establishment?.value,
      availableFromFrom: formattedDate.from,
      availableFromTo: formattedDate.to,
    },
    mapper: Mapper.mapWithTransformer<RewardItemData[], RewardItemTableItem[]>({
      transformer: RewardItemsTransformer.tableTransformer()
    })
  });

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] gap-6">
      <ListHeader
        Icon={<ShoppingBasket className="w-10 h-10" strokeWidth={1} />}
        title="Itens para resgate"
        actions={(
          <div className="flex items-center gap-2">
            <RedeemRewardItemModal />
            <Button
              disabled={rewardItemsIsPending || rewardItemsError?.status === 403}
              operationKlassToCheckPermission="Admin::Tmais::Products::Create"
              onClick={() => navigate('create')}
            >
              Cadastrar recompensa
            </Button>
          </div>
        )}
      />

      <ErrorMessageWrapper
        className="flex-1"
        statusCode={rewardItemsError?.status}
        refetchFunction={refetchRewardItems}
      >
        <div className="flex flex-col flex-1 overflow-auto gap-6">
          <div className="flex flex-col gap-4">
            <TextField
              name="search"
              LeftIcon={<SearchIcon />}
              placeholder="Pesquisar"
              className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              onChange={(e) => {
                setSearch((e.target as HTMLInputElement).value);
                setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }));
              }}
              value={filterParams.search}
            />

            <div className="flex items-center gap-2">
              <DatePickerWithRange
                placeholder="Data de disponibilidade"
                date={date}
                setDate={(date) => setFilterParams((prev) => ({ ...prev, date }))}
              />

              <ListFilters
                filters={[
                  {
                    className: 'min-w-40',
                    onChange: (e: any) => setFilterParams((params) => ({ ...params, status: e?.value ? e : null })),
                    value: filterParams?.status || '',
                    options: [{ label: 'Todas', value: '' }, ...rewardItemsStatusOptions],
                    placeholder: 'Status',
                    dataTestid: 'filter_status',
                  },
                  {
                    className: 'min-w-40',
                    onChange: (e: any) => setFilterParams((params) => ({ ...params, establishment: e?.value ? e : null })),
                    onInputChange: setSearchEstablishment,
                    value: filterParams?.establishment || '',
                    options: [{ label: 'Todos', value: '' }, ...establishmentOptions as Option[]],
                    placeholder: 'Estabelecimento',
                    dataTestid: 'filter_establishment',
                  },
                ]}
              />
            </div>
          </div>

          <RewardItemsGridTable
            className="max-h-[calc(100vh-284px)]"
            rewardItems={rewardItems || []}
            page={filterParams.page || 1}
            totalPages={meta.total_pages || 1}
            totalCount={meta.total}
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
            isLoadingData={rewardItemsIsLoading}
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  );
};

export default RewardItems;
